import { Store } from '@ngrx/store';
import { Component, Input, OnInit, ElementRef, NgZone, OnDestroy, OnChanges } from '@angular/core';
import { Observable, Subscription } from 'rxjs/Rx';

import { ResponseService } from '../../../../../../services/response.service';
import { PgRecorder } from '../../../../../common/audio-recording/pg-recorder';
import { LoggerService } from '../../../../../../services/logger.service';
import { TestNavigationService } from '../../../../../../services/test-navigation.service';
import { TestTakerService } from '../../../../../../services/test-taker.service';
import { TimerService } from '../../../../../../services/timer.service';
import { MyModal } from '../../../../../common/my-modal/my-modal.component';
import { AppUiActions } from './../../../../../../store/actions';
import * as fromRoot from './../../../../../../store/reducers';
import { ConfigHelper } from './../../../../../../services/config.helper';
import * as Enum from '../../../../../../dtos/enums';
import * as ErrorMessages from '../../../../../../errors/constants';

declare var _paragonConfig: PgRendererDto.ParagonAppConfig;

@Component({
    selector: 'speaking-interaction',
    template: require('./speaking-interaction.html')
})
export class SpeakingInteractionComponent implements OnInit, OnDestroy, OnChanges, PgRendererDto.InteractionComponentForPreflush {

    @Input() interaction: PgRendererDto.InteractionDefinition;

    readonly requiredSpikeCount: number = 2;
    readonly requiredSpikeThreshold: number = 10;

    speakingInteraction: PgRendererDto.SpeakingInteraction;
    prompt: string;
    preparationTime: number;
    recordingTime: number;
    currentProgress: any;
    infoBoxVisible: boolean;
    infoBoxText: string;
    infoBoxMode: string;
    iconCssClass: string;
    counterText: string;
    inputActivityLevel: number;
    volumeSpikes: number[];
    preparationSubscription: Subscription;
    recordingSubscription: Subscription;
    inputActivitySubscription: Subscription;
    keyDownSubscription: Subscription;
    recorder: PgRecorder;
    silentClipAudio: HTMLAudioElement;
    startSpeakingAudio: HTMLAudioElement;
    timeIsUpAudio: HTMLAudioElement;
    skipHotKeyPressed: boolean = false;
    isDummyRecordingMode: boolean = false;
    testTypeId: Enum.TestTypeEnum;

    constructor(
        private responseService: ResponseService,
        private loggerService: LoggerService,
        private testNavigationService: TestNavigationService,
        private testTakerService: TestTakerService,
        private myModal: MyModal,
        private store: Store<fromRoot.State>,
        private elementRef: ElementRef,
        private ngZone: NgZone,
        private timerService: TimerService,
    ) {
    }

    ngOnInit() {
        this.speakingInteraction = this.interaction.SpeakingInteraction;

        this.loggerService.reportDebugInfo(SpeakingInteractionComponent.name, {
            name: "ngOnInit",
            interactionId: this.interaction.InteractionId,
            preparationTime: this.speakingInteraction.PreparationTime,
            recordingTime: this.speakingInteraction.RecordingTime
        });

        this.prompt = this.speakingInteraction.Prompt;
        const extendPrep = this.speakingInteraction.ExtendPrepTime;
        const extendRecord = this.speakingInteraction.ExtendSpeakingTime;
        this.preparationTime = extendPrep ? this.speakingInteraction.PreparationTime * this.timerService.timer.multiplier : this.speakingInteraction.PreparationTime;
        this.recordingTime = extendRecord ? this.speakingInteraction.RecordingTime * this.timerService.timer.multiplier : this.speakingInteraction.RecordingTime;
        this.volumeSpikes = [];

        this.testTypeId = ConfigHelper.getTestTypeId();
        this.testNavigationService.setCanGoNext(false);
        this.startPreparation();
        this.initAudioClips();

        if (_paragonConfig.shortcutsEnabled === true) {
            this.keyDownSubscription = Observable.fromEvent<KeyboardEvent>(document, "keydown")
            .filter(x => x.keyCode == 114)
            .subscribe(x => this.skipHotKeyPressed = true);
        }

        this.testNavigationService.interactionComponentWithPreFlushProcessing = this;
    }

    ngOnDestroy() {
        this.testNavigationService.interactionComponentWithPreFlushProcessing = null;

        if (this.keyDownSubscription) {
            this.keyDownSubscription.unsubscribe();
        }
        if (this.preparationSubscription) {
            this.preparationSubscription.unsubscribe();
        }
        if (this.recordingSubscription) {
            this.recordingSubscription.unsubscribe();
        }
        if (this.inputActivitySubscription) {
            this.inputActivitySubscription.unsubscribe();
        }
    }

    ngOnChanges() {
        this.infoBoxVisible = false;
    }

    runPreFlushProcessing() {
        if (this.recorder && this.recorder.mediaRecorder.state == "recording") {
            this.recorder.onStopCallback = () => { null };
            this.responseService.setResponse(this.interaction, null, null, null, false, null);
            this.responseService.setSpeakingBlobData(this.interaction.InteractionId, this.recorder.getBlob());
            this.stopRecording();
        }
    }

    initAudioClips() {
        let startSpeakingFile = "start_speaking.mp3";
        let timeIsUpFile = "time_is_up.mp3";
        if (ConfigHelper.shouldUseBeepAudioClipForSpeaking(this.testTypeId)) {
            startSpeakingFile = "olt_start_speaking.mp3";
            timeIsUpFile = "olt_time_is_up.mp3";
        }
        this.silentClipAudio = this.createAudioElement("silent_clip.mp3");
        this.startSpeakingAudio = this.createAudioElement(startSpeakingFile);
        this.timeIsUpAudio = this.createAudioElement(timeIsUpFile);
    }

    startPreparation() {
        this.loggerService.reportDebugInfo(SpeakingInteractionComponent.name, {
            name: "startPreparation"
        });

        this.infoBoxVisible = true;
        this.infoBoxText = "Preparation Time";
        this.infoBoxMode = "CountDownTimer";
        this.iconCssClass = 'clock-icon';
        this.counterText = this.preparationTime.toString();
        this.inputActivityLevel = 100;
        this.preparationSubscription = Observable
            .interval(1000)
            .switchMap(() => this.timerService.isPaused() ? Observable.of(false) : Observable.of(true))
            .scan((acc, isPaused) => isPaused ? acc - 1 : acc, this.preparationTime)
            .takeWhile(currPrepTime => currPrepTime >= 0)
            .subscribe(timeLeft => {
                this.counterText = timeLeft > 0 ? timeLeft.toString() : "--";
                if (this.skipHotKeyPressed == true || timeLeft <= 0) {
                    this.skipHotKeyPressed = false;
                    this.startRecording(true);
                }
            });
    }

    startRecording(useRecordingDevice: boolean) {
        this.isDummyRecordingMode = !useRecordingDevice;
        this.infoBoxMode = "TextOnly";
        this.infoBoxText = "";
        this.silentClipAudio.play();
        if (useRecordingDevice) {
            PgRecorder.create(this.loggerService)
                .then(recorder => {
                    this.recorder = recorder;
                    this.startSpeakingAudio.play();
                    this.initializeRecordingDisplay();

                    this.recorder.start(this.stopRecordingCallback);
                    this.recordingSubscription = Observable.interval(1000)
                        .take(this.recordingTime)
                        .subscribe(x => {
                            const timeElapsed = x + 1;
                            this.currentProgress = this.setProgress(timeElapsed, this.recordingTime);
                            if (this.skipHotKeyPressed == true || timeElapsed >= this.recordingTime) {
                                this.stopRecording();
                            }});

                    this.testTakerService.cacheTestTakerActivity(Enum.TestTakerActivityTypeEnum.StartSpeaking, null, null, null, this.interaction.InteractionId, null);

                    this.inputActivitySubscription = Observable.interval(200)
                        .subscribe(x => {
                            const inputActivityLevel = this.recorder.getInputActivityLevel();
                            this.inputActivityLevel = 100 - inputActivityLevel;
                            if (this.volumeSpikes.length < this.requiredSpikeCount && inputActivityLevel >= this.requiredSpikeThreshold) {
                                this.volumeSpikes.push(inputActivityLevel);
                            }
                        });
                })
                .catch(error => {
                    this.loggerService.reportError(SpeakingInteractionComponent.name, error);
                    if (ConfigHelper.isIppMode()) {
                        this.startRecording(false);
                    } else {
                        this.myModal.showRegularDlg(
                            "Error",
                            ConfigHelper.isTdpMode() ? ErrorMessages.SpeakingInteraction_Recorder_Start_With_Code : ErrorMessages.SpeakingInteraction_Recorder_Start,
                            [{ label: "Retry", style: "btn-primary" }])
                            .then(x => this.startRecording(true));
                    }
                });
        } else {
            this.startDummyRecording();
        }
    }

    initializeRecordingDisplay() {
        if (this.preparationSubscription) {
            this.preparationSubscription.unsubscribe();
        }

        if (this.recordingSubscription) {
            this.recordingSubscription.unsubscribe();
        }

        if (this.inputActivitySubscription) {
            this.inputActivitySubscription.unsubscribe();
        }

        this.loggerService.reportDebugInfo(SpeakingInteractionComponent.name, {
            name: "startRecording"
        });

        this.infoBoxVisible = true;
        this.infoBoxText = "Recording...";
        this.infoBoxMode = "ProgressBar";
        this.iconCssClass = 'rec-icon';
        this.currentProgress = 0;
    }

    startDummyRecording() {
        this.startSpeakingAudio.play();
        this.initializeRecordingDisplay();
        this.recordingSubscription = Observable.interval(1000)
            .take(this.recordingTime)
            .subscribe(x => {
                const timeElapsed = x + 1;
                this.currentProgress = this.setProgress(timeElapsed, this.recordingTime);
                if (this.skipHotKeyPressed == true || timeElapsed >= this.recordingTime) {
                    this.stopRecording();
                }});
    }

    stopRecording() {
        this.loggerService.reportDebugInfo(SpeakingInteractionComponent.name, {
            name: "stopRecording"
        });

        if (this.recordingSubscription) {
            this.recordingSubscription.unsubscribe();
        }

        if (this.inputActivitySubscription) {
            this.inputActivitySubscription.unsubscribe();
        }

        this.inputActivityLevel = 100;

        if (this.isDummyRecordingMode == true) {
            this.stopDummyRecording();
        } else {
            this.recorder.stop();
        }
    }

    stopDummyRecording() {
        this.ngZone.run(() => {
            this.timeIsUpAudio.play();
            this.infoBoxText = "Finished recording.";
            const timesUpCallback = () => this.testNavigationService.goNext(false);
            this.myModal.showTimesUpDlg(timesUpCallback);
        });
    }

    private stopRecordingCallback = () => {
        this.ngZone.run(() => {
            this.timeIsUpAudio.play();
            if (this.skipHotKeyPressed == true || this.isRecordingAcceptable()) {
                this.infoBoxText = "Finished recording.";
                if (ConfigHelper.isIppMode()) { this.responseService.setResponse(this.interaction, null, null, null, false, null); }
                // ignore the speaking blob if the timer is paused since the test taker will be given another recording attempt once the timer is unpaused
                if (!this.timerService.isPaused()) {
                    this.responseService.setSpeakingBlobData(this.interaction.InteractionId, this.recorder.getBlob());
                }
                const timesUpCallback = () => {
                    this.testNavigationService.goNext(false);
                };
                if (ConfigHelper.shouldShowTimesUpDlgForSpeaking(this.testTypeId) === true) {
                    this.myModal.showTimesUpDlg(timesUpCallback);
                } else {
                    setTimeout(() => {
                        timesUpCallback();
                    }, 3000);
                }
            } else {
                this.store.dispatch(new AppUiActions.ShowGenericPage("Unacceptable Recording", ErrorMessages.SpeakingInteraction_Times_Up, true));
            }
        });
    }

    setProgress(current: number, totalTime: number): number {
        return (current / totalTime) * 100;
    }

    isRecordingAcceptable(): boolean {
        if (ConfigHelper.shouldCheckForUnacceptableRecording(this.testTypeId) && !this.timerService.isPaused()) {
            return this.volumeSpikes.length >= this.requiredSpikeCount;
        }
        return true;
    }

    createAudioElement(src: string) {
        const elem = (this.elementRef.nativeElement as HTMLElement);
        const audioElem = document.createElement("audio");
        audioElem.autoplay = false;
        audioElem.controls = false;
        audioElem.autoplay = false;
        audioElem.src = `${ConfigHelper.getAudioAssetUrl()}/${src}`;
        elem.appendChild(audioElem);
        return audioElem;
    }
}



// WEBPACK FOOTER //
// C:/_/Platform1/Paragon/Application/Common/UI.RenderEngine/src/app/components/pg-renderer/item-body/item-body-panel/interaction/speaking-interaction/speaking-interaction.component.ts